import React, { useState, useMemo, useEffect } from "react";
import { Spin, Button, Tooltip, Input, Select, Dropdown, Avatar } from "antd";
import {
  PlusOutlined, SearchOutlined,
  StarFilled, StarOutlined, ClockCircleOutlined, DownOutlined,
} from "@ant-design/icons";
import { motion, AnimatePresence } from "framer-motion";
import { TICKET_STATUS } from "./constants";
import { _fetchAgents, getAgentColor, agentInitials } from "./helpers";
import { ActiveChatCard } from "./ActiveChatCard";

export function ChatSidebarColumn({
  col,
  activeChats, setActiveChats,
  chatsLoading, chatsLoadingMore, hasMoreChats,
  chatSearch, setChatSearch, chatSearchFilter, setChatSearchFilter,
  fetchActiveChats, loadMoreChats,
  selectedChat, setSelectedChat,
  queryMatchesByPhone, scrollToSingleQuery, flashingChatIds,
  authState,
  onBulkTemplateOpen,
  chatScrollRef,
}) {
  const [starredFilter, setStarredFilter] = useState(false);
  const [unreadOnlyFilter, setUnreadOnlyFilter] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [agentFilter, setAgentFilter] = useState(null);
  const [filterAgents, setFilterAgents] = useState([]);
  const [filterAgentsLoading, setFilterAgentsLoading] = useState(false);
  const [expirySortOn, setExpirySortOn] = useState(false);

  useEffect(() => {
    if (unreadOnlyFilter && activeChats.filter(c => c.unread_count > 0).length === 0) {
      setUnreadOnlyFilter(false);
    }
  }, [activeChats, unreadOnlyFilter]);

  const visibleChats = useMemo(() => {
    let list = activeChats;
    if (unreadOnlyFilter) list = list.filter(c => c.unread_count > 0);
    if (starredFilter)    list = list.filter(c => c.starred);
    if (statusFilter !== null) list = list.filter(c => c.ticket_status === statusFilter);
    if (agentFilter === "__unassigned") list = list.filter(c => !c.agent);
    else if (agentFilter) list = list.filter(c => c.agent === agentFilter);
    if (expirySortOn) list = [...list].sort((a, b) => {
      const aMs = a.expiry?.isValid() ? a.expiry.valueOf() : Infinity;
      const bMs = b.expiry?.isValid() ? b.expiry.valueOf() : Infinity;
      return aMs - bMs;
    });
    return list;
  }, [activeChats, unreadOnlyFilter, starredFilter, statusFilter, agentFilter, expirySortOn]);

  return (
    <div
      style={{
        minWidth: 280, maxWidth: 280,
        display: "flex", flexDirection: "column",
        height: "100%",
        backgroundColor: "#fff",
        borderRadius: 8,
        border: `2px solid ${col.color}33`,
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      {/* Column header */}
      <div style={{
        padding: "10px 12px",
        backgroundColor: col.color + "18",
        borderBottom: `2px solid ${col.color}`,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexShrink: 0,
      }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: col.color, display: "flex", alignItems: "center", gap: 6 }}>
          {col.icon}
          {col.label}
          <Tooltip title={unreadOnlyFilter ? "Show all chats" : "Show unread only"}>
            <motion.span
              onClick={() => setUnreadOnlyFilter(v => !v)}
              animate={
                !unreadOnlyFilter && activeChats.filter(c => c.unread_count > 0).length > 0
                  ? { boxShadow: ["0 0 0px 0px rgba(37,211,102,0)", "0 0 7px 3px rgba(37,211,102,0.65)", "0 0 0px 0px rgba(37,211,102,0)"] }
                  : { boxShadow: "0 0 0px 0px rgba(37,211,102,0)" }
              }
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
              style={{
                cursor: "pointer", userSelect: "none",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                minWidth: 22, height: 18, padding: "0 6px",
                borderRadius: 10, fontSize: 11, fontWeight: 700,
                background: unreadOnlyFilter ? "#25d366" : "#e6ffe6",
                color: unreadOnlyFilter ? "#fff" : "#25d366",
                border: `1.5px solid ${unreadOnlyFilter ? "#25d366" : "#b7eb8f"}`,
                transition: "background 0.15s, color 0.15s, border-color 0.15s",
              }}
            >
              {activeChats.filter(c => c.unread_count > 0).length}
            </motion.span>
          </Tooltip>
        </span>
        <Tooltip title="Bulk template send">
          <Button
            size="small"
            icon={<PlusOutlined />}
            type="primary"
            ghost
            style={{ padding: "0 7px", height: 22, fontSize: 11, borderRadius: 6 }}
            onClick={onBulkTemplateOpen}
          />
        </Tooltip>
      </div>

      {/* Search bar */}
      <div style={{ padding: "6px 8px", flexShrink: 0, borderBottom: "1px solid #f0f0f0", background: "#fff" }}>
        <Input size="small"
          placeholder={chatSearchFilter === 0 ? "Search by name or number…" : "Search in messages…"}
          value={chatSearch}
          onChange={e => {
            setChatSearch(e.target.value);
            if (!e.target.value) fetchActiveChats("", chatSearchFilter);
          }}
          onPressEnter={() => fetchActiveChats()} allowClear
          prefix={
            <div onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
              <Select
                value={chatSearchFilter}
                onChange={val => setChatSearchFilter(val)}
                size="small"
                bordered={false}
                getPopupContainer={() => document.body}
                style={{ marginLeft: -7, marginRight: 2, fontSize: 11, minWidth: 78 }}
                dropdownStyle={{ minWidth: 130 }}
              >
                <Select.Option value={0}>Name/No.</Select.Option>
                <Select.Option value={1}>Messages</Select.Option>
              </Select>
            </div>
          }
          suffix={<SearchOutlined style={{ color: "#8c8c8c", cursor: "pointer" }} onClick={() => fetchActiveChats()} />}
          style={{ borderRadius: 6, fontSize: 12 }}
        />
      </div>

      {/* Filter bar */}
      <div style={{ padding: "4px 8px", flexShrink: 0, borderBottom: "1px solid #f0f0f0", background: "#fff", display: "flex", alignItems: "center", gap: 5 }}>
        <Tooltip title={starredFilter ? "Show all chats" : "Show starred only"}>
          <span
            onClick={() => setStarredFilter(v => !v)}
            style={{ cursor: "pointer", color: starredFilter ? "#faad14" : "#bfbfbf", fontSize: 13, display: "flex", alignItems: "center", transition: "color 0.2s", marginRight: 2 }}
          >
            {starredFilter ? <StarFilled /> : <StarOutlined />}
          </span>
        </Tooltip>

        {/* Status filter */}
        <Dropdown
          trigger={["click"]}
          getPopupContainer={() => document.body}
          menu={{
            selectedKeys: statusFilter != null ? [String(statusFilter)] : ["all"],
            items: [
              { key: "all", label: <span style={{ fontSize: 11, color: "#8c8c8c" }}>All statuses</span>, onClick: () => setStatusFilter(null) },
              { key: "1", label: <span style={{ color: TICKET_STATUS[1].color, fontWeight: 600, fontSize: 11 }}>Open</span>, onClick: () => setStatusFilter(v => v === 1 ? null : 1) },
              { key: "3", label: <span style={{ color: TICKET_STATUS[3].color, fontWeight: 600, fontSize: 11 }}>Pending</span>, onClick: () => setStatusFilter(v => v === 3 ? null : 3) },
              { key: "2", label: <span style={{ color: TICKET_STATUS[2].color, fontWeight: 600, fontSize: 11 }}>Closed</span>, onClick: () => setStatusFilter(v => v === 2 ? null : 2) },
            ],
          }}
        >
          {(() => {
            const s = statusFilter != null ? TICKET_STATUS[statusFilter] : null;
            return (
              <span style={{
                cursor: "pointer", fontSize: 10, padding: "1px 7px", borderRadius: 10,
                border: `1px solid ${s ? s.border : "#d9d9d9"}`,
                color: s ? s.color : "#8c8c8c",
                background: s ? s.bg : "transparent",
                fontWeight: s ? 600 : 400,
                transition: "all 0.15s", userSelect: "none",
                display: "inline-flex", alignItems: "center", gap: 3,
              }}>
                {s ? s.label : "Status"} <DownOutlined style={{ fontSize: 7 }} />
              </span>
            );
          })()}
        </Dropdown>

        {/* Agent filter */}
        <Dropdown
          trigger={["click"]}
          getPopupContainer={() => document.body}
          onOpenChange={open => {
            if (!open) return;
            setFilterAgentsLoading(true);
            _fetchAgents().then(list => { setFilterAgents(list); setFilterAgentsLoading(false); });
          }}
          menu={{
            selectedKeys: agentFilter != null ? [agentFilter] : ["__all"],
            items: (() => {
              const _makeFilterItem = a => ({
                key: a.fullName,
                label: (
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <div style={{ position: "relative", flexShrink: 0 }}>
                      <Avatar size={20} style={{ backgroundColor: getAgentColor(a.fullName), fontSize: 9, fontWeight: 700 }}>
                        {agentInitials(a.fullName)}
                      </Avatar>
                      <span style={{ position: "absolute", bottom: 0, right: -1, width: 6, height: 6, borderRadius: "50%", background: (a.online || a.isBot) ? "#52c41a" : "#bfbfbf", border: "1.5px solid #fff" }} />
                    </div>
                    <span style={{ fontSize: 11 }}>{a.fullName}</span>
                  </div>
                ),
                onClick: () => setAgentFilter(v => v === a.fullName ? null : a.fullName),
              });
              const _filterSectionLabel = (text) => {
                const isOnline = text === "ONLINE";
                return {
                  key: `__sec_${text}`,
                  label: (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "2px 4px" }}>
                      <div style={{ flex: 1, height: 1, background: isOnline ? "#b7eb8f" : "#f0f0f0" }} />
                      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.05em", color: isOnline ? "#52c41a" : "#bfbfbf", whiteSpace: "nowrap" }}>{text}</span>
                      <div style={{ flex: 1, height: 1, background: isOnline ? "#b7eb8f" : "#f0f0f0" }} />
                    </div>
                  ),
                  disabled: true,
                };
              };
              const online  = filterAgents.filter(a =>  (a.online || a.isBot));
              const offline = filterAgents.filter(a => !(a.online || a.isBot));
              return [
                { key: "__all", label: <span style={{ fontSize: 11, color: "#8c8c8c" }}>All agents</span>, onClick: () => setAgentFilter(null) },
                { key: "__unassigned", label: <span style={{ fontSize: 11, color: "#595959" }}>Unassigned</span>, onClick: () => setAgentFilter(v => v === "__unassigned" ? null : "__unassigned") },
                { key: "__div0", label: <div style={{ height: 1, background: "#f0f0f0", margin: "2px 8px" }} />, disabled: true },
                ...(filterAgentsLoading
                  ? [{ key: "__loading", label: <div style={{ textAlign: "center", padding: "4px 0" }}><Spin size="small" /></div>, disabled: true }]
                  : [
                    ...(online.length > 0 ? [_filterSectionLabel("ONLINE")] : []),
                    ...online.map(_makeFilterItem),
                    ...(offline.length > 0 ? [_filterSectionLabel("OFFLINE")] : []),
                    ...offline.map(_makeFilterItem),
                  ]
                ),
              ];
            })(),
          }}
          dropdownRender={menu => (
            <div style={{ maxHeight: 260, overflowY: "auto", borderRadius: 8 }}>
              {menu}
            </div>
          )}
        >
          {(() => {
            const label = agentFilter === "__unassigned" ? "Unassigned"
              : agentFilter ? agentFilter.split(" ")[0]
              : null;
            return (
              <span style={{
                cursor: "pointer", fontSize: 10, padding: "1px 7px", borderRadius: 10,
                border: "1px solid #d9d9d9",
                color: agentFilter ? "#262626" : "#8c8c8c",
                background: agentFilter ? "#f5f5f5" : "transparent",
                fontWeight: agentFilter ? 600 : 400,
                transition: "all 0.15s", userSelect: "none",
                display: "inline-flex", alignItems: "center", gap: 3,
                maxWidth: 72, overflow: "hidden",
              }}>
                {agentFilter && (
                  <Avatar size={12} style={{ backgroundColor: getAgentColor(agentFilter === "__unassigned" ? "" : agentFilter), fontSize: 7, fontWeight: 700, flexShrink: 0 }}>
                    {agentFilter === "__unassigned" ? "?" : agentInitials(agentFilter)}
                  </Avatar>
                )}
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {label ?? "Agent"}
                </span>
                <DownOutlined style={{ fontSize: 7, flexShrink: 0 }} />
              </span>
            );
          })()}
        </Dropdown>

        {/* Expiry sort */}
        <Tooltip title={expirySortOn ? "Remove expiry sort" : "Sort by expiry (soonest first)"}>
          <span
            onClick={() => setExpirySortOn(v => !v)}
            style={{
              cursor: "pointer", fontSize: 10, padding: "1px 6px", borderRadius: 10,
              border: `1px solid ${expirySortOn ? "#ff4d4f" : "#d9d9d9"}`,
              color: expirySortOn ? "#ff4d4f" : "#8c8c8c",
              background: expirySortOn ? "#fff1f0" : "transparent",
              fontWeight: expirySortOn ? 600 : 400,
              transition: "all 0.15s", userSelect: "none",
              display: "inline-flex", alignItems: "center", gap: 3,
            }}
          >
            <ClockCircleOutlined style={{ fontSize: 9 }} />↓
          </span>
        </Tooltip>

        <span style={{ fontSize: 10, color: "#8c8c8c", flexShrink: 0, marginLeft: "auto" }}>
          {visibleChats.length} shown
        </span>
      </div>

      {/* Cards scroll area */}
      <div
        ref={chatScrollRef}
        onScroll={e => {
          const el = e.currentTarget;
          if (el.scrollHeight - el.scrollTop - el.clientHeight < 220) loadMoreChats();
        }}
        style={{ flex: 1, overflowY: "auto", padding: 8, display: "flex", flexDirection: "column", gap: 8 }}
      >
        {chatsLoading
          ? <div style={{ display: "flex", justifyContent: "center", paddingTop: 32 }}><Spin /></div>
          : visibleChats.length === 0
            ? <div style={{ textAlign: "center", color: "#bfbfbf", marginTop: 40, fontSize: 13 }}>No chats</div>
            : (
              <>
                <AnimatePresence initial={false}>
                  {visibleChats.map(chat => (
                    <motion.div
                      key={chat.id}
                      id={`chat-card-${chat.id}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.15 }}
                      style={{
                        borderRadius: 8,
                        boxShadow: flashingChatIds.has(chat.id) ? "0 0 0 2px #00a884" : undefined,
                        transition: "box-shadow 0.3s",
                      }}
                    >
                      <ActiveChatCard
                        chat={chat}
                        isSelected={selectedChat?.id === chat.id}
                        onClick={() => setSelectedChat(chat)}
                        matchingQueries={queryMatchesByPhone[(chat.phone || "").replace(/\D/g, "")] || []}
                        onQueryJump={scrollToSingleQuery}
                        onChatPatch={fields => {
                          setActiveChats(cs => cs.map(c => c.id === chat.id ? { ...c, ...fields } : c));
                          if (selectedChat?.id === chat.id) setSelectedChat(c => ({ ...c, ...fields }));
                        }}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
                {!hasMoreChats && activeChats.length > 0 && (
                  <div style={{ textAlign: "center", fontSize: 11, color: "#8c8c8c", padding: "6px 0" }}>All chats loaded</div>
                )}
              </>
            )
        }
      </div>

      {chatsLoadingMore && (
        <div style={{ flexShrink: 0, padding: "6px 8px", borderTop: "1px solid #f0f0f0", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: "#fff" }}>
          <Spin size="small" />
          <span style={{ fontSize: 11, color: "#8c8c8c" }}>Loading more chats…</span>
        </div>
      )}
    </div>
  );
}
